import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Switch,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import AxiosInstance from "../../services/networkServices";
import { showToast } from "../../store/slices/common";

const formatDateTime = (value) =>
  value ? dayjs(value).format("MMM D, YYYY h:mm A") : null;

// Index matches the stored dayOfWeek: 0 = Sunday … 6 = Saturday
const DAYS_OF_WEEK = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

// "HH:mm" -> dayjs on today's date (only the time-of-day matters)
const timeStringToDayjs = (time) => {
  if (!time) return null;
  const [hours, minutes] = time.split(":").map(Number);
  return dayjs().hour(hours).minute(minutes).second(0).millisecond(0);
};

let periodRowKey = 0;
const newPeriodRow = (period = {}) => {
  const dayOfWeek = period.dayOfWeek ?? 5;
  let endDayOfWeek = period.endDayOfWeek;
  if (!Number.isInteger(endDayOfWeek)) {
    const wrapsOvernight =
      period.startTime &&
      period.endTime &&
      period.endTime <= period.startTime;
    endDayOfWeek = wrapsOvernight ? (dayOfWeek + 1) % 7 : dayOfWeek;
  }
  return {
    key: (periodRowKey += 1),
    dayOfWeek,
    endDayOfWeek,
    startTime: timeStringToDayjs(period.startTime),
    endTime: timeStringToDayjs(period.endTime),
  };
};

const blackInputSx = {
  "& .MuiInputBase-input": { color: "black !important" },
};

/**
 * Weekly availability card. The schedule is the only gate: the feature is
 * available during the windows below and unavailable otherwise. A window can
 * start on one day and end on a later day (Friday 5:00 PM until Monday
 * 12:00 AM) and stays open the whole time in between. The same day and
 * clock time (Friday 5:00 PM until Friday 5:00 PM) means always on. When
 * `showNotificationsToggle` is set, a switch controls the "now available"
 * push sent when a window opens.
 *
 * Shared by the Rap page, the Karaoke page, the Dance page, and Go Live
 * on Match & Energy. `showMasterToggle` adds an on/off switch (Go Live).
 * `alwaysOnWithoutWindows` means an empty schedule stays available while
 * that switch is on, instead of meaning unavailable.
 *
 * @param {object} props
 * @param {string} props.title Card heading
 * @param {string} props.featureLabel Feature name used in copy ("Rap battles")
 * @param {string} props.endpoint GET/PUT path, e.g. "/admin/settings/rap-availability"
 * @param {string} props.responseKey Key of the availability object in the response
 * @param {boolean} [props.showNotificationsToggle] Show the push-notification switch
 * @param {boolean} [props.showMasterToggle] Show an on/off switch sent as `enabled`
 * @param {boolean} [props.alwaysOnWithoutWindows] Empty windows stay available while the switch is on
 * @param {string} [props.statusVerb] Verb in the status line ("are" or "is")
 * @param {string} [props.scheduleHelp] Copy shown under the status line
 */
const LiveAvailabilityCard = ({
  title = "Availability",
  featureLabel = "Rap battles",
  endpoint = "/admin/settings/rap-availability",
  responseKey = "rapAvailability",
  showNotificationsToggle = true,
  showMasterToggle = false,
  alwaysOnWithoutWindows = false,
  statusVerb = "are",
  scheduleHelp,
}) => {
  const dispatch = useDispatch();
  const featureLower = featureLabel.toLowerCase();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [periodRows, setPeriodRows] = useState([]);
  // Master on/off. Only used when showMasterToggle is set (Go Live).
  const [enabled, setEnabled] = useState(true);
  // "Rap battles available" push broadcast on unavailable -> available flips
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  // Last saved server state (drives the effective-status line)
  const [savedState, setSavedState] = useState(null);

  const applyState = (availability) => {
    if (!availability) return;
    const weeklySchedule = availability.weeklySchedule || {
      enabled: false,
      periods: [],
    };
    setPeriodRows((weeklySchedule.periods || []).map(newPeriodRow));
    if (showMasterToggle) setEnabled(availability.enabled !== false);
    // Older server responses omit the flag; the backend treats missing as on.
    setNotificationsEnabled(availability.notificationsEnabled !== false);
    setSavedState(availability);
  };

  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        setLoading(true);
        const response = await AxiosInstance.get(endpoint);
        if (response.data.status) {
          applyState(response.data.data[responseKey]);
        } else {
          dispatch(
            showToast({
              color: "error",
              msg: `Failed to load ${featureLower} availability.`,
            })
          );
        }
      } catch (error) {
        console.error(`Error fetching ${featureLower} availability:`, error);
        dispatch(
          showToast({
            color: "error",
            msg: `Failed to load ${featureLower} availability.`,
          })
        );
      } finally {
        setLoading(false);
      }
    };
    fetchAvailability();
  }, [endpoint, responseKey]);

  const addPeriodRow = () =>
    setPeriodRows((rows) => [...rows, newPeriodRow()]);

  const removePeriodRow = (key) =>
    setPeriodRows((rows) => rows.filter((row) => row.key !== key));

  const updatePeriodRow = (key, patch) =>
    setPeriodRows((rows) =>
      rows.map((row) => (row.key === key ? { ...row, ...patch } : row))
    );

  /** Serialize the weekly schedule, or return an error message to toast. */
  const buildWeeklySchedule = () => {
    if (periodRows.length === 0) {
      return { weeklySchedule: { enabled: false, periods: [] } };
    }
    if (periodRows.some((row) => !row.startTime || !row.endTime)) {
      return { error: "Every window needs both a start and an end time." };
    }
    // Same day + same clock time is intentional: a full week of continuous
    // coverage (e.g. Friday 5:00 PM → Friday 5:00 PM = always available).
    return {
      weeklySchedule: {
        enabled: true,
        periods: periodRows.map((row) => ({
          dayOfWeek: row.dayOfWeek,
          startTime: row.startTime.format("HH:mm"),
          endDayOfWeek: row.endDayOfWeek,
          endTime: row.endTime.format("HH:mm"),
        })),
      },
    };
  };

  const handleSave = async () => {
    const weekly = buildWeeklySchedule();
    if (weekly.error) {
      dispatch(showToast({ color: "error", msg: weekly.error }));
      return;
    }
    try {
      setSaving(true);
      const payload = {
        weeklySchedule: weekly.weeklySchedule,
        ...(showMasterToggle && { enabled }),
        ...(showNotificationsToggle && { notificationsEnabled }),
      };
      const response = await AxiosInstance.put(endpoint, payload);
      if (response.data.status) {
        if (response.data.data?.[responseKey]) {
          applyState(response.data.data[responseKey]);
        }
        dispatch(
          showToast({
            color: "success",
            msg: `${featureLabel} availability saved.`,
          })
        );
      } else {
        dispatch(
          showToast({
            color: "error",
            msg:
              response.data.message ||
              `Failed to save ${featureLower} availability.`,
          })
        );
      }
    } catch (error) {
      console.error(`Error saving ${featureLower} availability:`, error);
      dispatch(
        showToast({
          color: "error",
          msg:
            error?.response?.data?.message ||
            `Failed to save ${featureLower} availability.`,
        })
      );
    } finally {
      setSaving(false);
    }
  };

  const renderStatusLine = () => {
    if (!savedState) return null;
    const available = savedState.isCurrentlyAvailable;
    // Prefer the computed next flips (they fold in the weekly schedule);
    // fall back to the raw one-shot dates for older server responses.
    const reopenSource = !available
      ? savedState.nextAvailableAt ?? savedState.availableAt
      : null;
    const closeSource = available
      ? savedState.nextUnavailableAt ?? savedState.unavailableAt
      : null;
    const reopenDate = reopenSource ? formatDateTime(reopenSource) : null;
    const closeDate =
      closeSource && dayjs(closeSource).isAfter(dayjs())
        ? formatDateTime(closeSource)
        : null;

    let text = available
      ? `${featureLabel} ${statusVerb} currently AVAILABLE`
      : `${featureLabel} ${statusVerb} currently UNAVAILABLE`;
    if (reopenDate) text += `. Scheduled to reopen ${reopenDate}`;
    if (closeDate) text += `. Scheduled to close ${closeDate}`;

    return (
      <Typography
        sx={{
          fontSize: "13px",
          fontWeight: 600,
          color: available ? "#1b7a2f" : "#b3261e",
        }}
      >
        {text}
      </Typography>
    );
  };

  const renderNotifications = () => (
    <Box sx={{ flexBasis: "100%" }}>
      <FormControlLabel
        control={
          <Switch
            checked={notificationsEnabled}
            onChange={(event) => setNotificationsEnabled(event.target.checked)}
            disabled={saving}
          />
        }
        label={`Notify users when ${featureLower} become available`}
      />
      <Typography sx={{ fontSize: "12px", color: "#666" }}>
        {notificationsEnabled
          ? `When a scheduled window opens, every user with push notifications on gets a “${featureLabel} available” push. If rap battles and live karaoke are set to open at the same time, users get one push that announces both.`
          : `No push is sent when ${featureLower} become available. Availability itself is unaffected.`}
      </Typography>
    </Box>
  );

  const renderDaySelect = (value, onChange) => (
    <Select
      size="small"
      value={value}
      onChange={onChange}
      disabled={saving}
      sx={{ width: "140px", color: "black" }}
    >
      {DAYS_OF_WEEK.map((day, index) => (
        <MenuItem key={day} value={index}>
          {day}
        </MenuItem>
      ))}
    </Select>
  );

  const renderWeeklySchedule = () => (
    <Box sx={{ flexBasis: "100%" }}>
      <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#000" }}>
        Weekly windows
      </Typography>
      <Typography sx={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
        {alwaysOnWithoutWindows
          ? `${featureLabel} ${statusVerb} available all week while the switch is on and there are no windows. Add windows to limit ${featureLower} to those times. A window can run across days — for example Friday 5:00 PM until Monday 12:00 AM. The same day and time keeps it available all week. Times are in ${
              savedState?.timezone || "the app timezone"
            }.`
          : `${featureLabel} are available only during these windows, and unavailable the rest of the week. A window can run across days — for example Friday 5:00 PM until Monday 12:00 AM — and stays open the whole time in between. The same day and time (Friday 5:00 PM until Friday 5:00 PM) keeps ${featureLower} available all week. Times are in ${
              savedState?.timezone || "the app timezone"
            }.`}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          marginTop: "12px",
        }}
      >
        {periodRows.length === 0 && (
          <Typography sx={{ fontSize: "13px", color: "#666" }}>
            {alwaysOnWithoutWindows
              ? `No windows yet. While the switch is on, ${featureLower} stays available all week.`
              : `No windows yet. ${featureLabel} stay unavailable until you add one.`}
          </Typography>
        )}
        {periodRows.map((row) => (
          <Box
            key={row.key}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flexWrap: "wrap",
            }}
          >
            {renderDaySelect(row.dayOfWeek, (event) =>
              updatePeriodRow(row.key, { dayOfWeek: Number(event.target.value) })
            )}
            <TimePicker
              label="From"
              value={row.startTime}
              onChange={(value) =>
                updatePeriodRow(row.key, { startTime: value })
              }
              ampm={true}
              disabled={saving}
              slotProps={{ textField: { size: "small" } }}
              sx={{ width: "150px", ...blackInputSx }}
            />
            {renderDaySelect(row.endDayOfWeek, (event) =>
              updatePeriodRow(row.key, {
                endDayOfWeek: Number(event.target.value),
              })
            )}
            <TimePicker
              label="Until"
              value={row.endTime}
              onChange={(value) =>
                updatePeriodRow(row.key, { endTime: value })
              }
              ampm={true}
              disabled={saving}
              slotProps={{ textField: { size: "small" } }}
              sx={{ width: "150px", ...blackInputSx }}
            />
            <IconButton
              aria-label="Remove window"
              size="small"
              onClick={() => removePeriodRow(row.key)}
              disabled={saving}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <Box>
          <Button
            variant="outlined"
            onClick={addPeriodRow}
            disabled={saving}
            sx={{
              color: "#142C5C",
              borderColor: "#142C5C",
              "&:hover": { borderColor: "#0f2247" },
              borderRadius: "6px",
              textTransform: "none",
            }}
          >
            Add window
          </Button>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        padding: "15px",
        borderRadius: "6px",
        boxShadow:
          "rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px",
        marginTop: "20px",
      }}
    >
      <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#000" }}>
        {title}
      </Typography>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", padding: "20px" }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "20px",
              marginTop: "10px",
            }}
          >
            {renderWeeklySchedule()}
            {showMasterToggle && (
              <Box sx={{ flexBasis: "100%" }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={enabled}
                      onChange={(event) => setEnabled(event.target.checked)}
                      disabled={saving}
                    />
                  }
                  label={`${featureLabel} is available`}
                />
                <Typography sx={{ fontSize: "12px", color: "#666" }}>
                  {enabled
                    ? `Turn this off to hide ${featureLower} immediately. Windows below only apply while this stays on.`
                    : `${featureLabel} stays hidden until you turn this back on. Any windows you save are kept for later.`}
                </Typography>
              </Box>
            )}
            {showNotificationsToggle && renderNotifications()}
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              sx={{
                backgroundColor: "#142C5C",
                color: "#fff",
                "&:hover": { backgroundColor: "#0f2247" },
                borderRadius: "6px",
                textTransform: "none",
              }}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
            <Box sx={{ flexBasis: "100%" }}>
              {renderStatusLine()}
              <Typography sx={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                {scheduleHelp ||
                  `Users can queue for ${featureLower} only while a window is open. The app shows “until” during a window and “back on” before the next one.`}
              </Typography>
            </Box>
          </Box>
        </LocalizationProvider>
      )}
    </Box>
  );
};

export default LiveAvailabilityCard;
